// Public API service for organization directory (no authentication required)
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

class PublicOrganizationService {
  /**
   * Get all public organizations with pagination
   * @param {number} limit - Number of organizations to fetch (default: 50)
   * @param {string} lastKey - Last evaluated key for pagination (optional)
   * @returns {Promise<Object>} Organizations data with pagination info
   */
  static async getPublicOrganizations(limit = 50, lastKey = null) {
    try {
      const params = new URLSearchParams({
        limit: limit.toString()
      });

      if (lastKey) {
        params.append('lastKey', lastKey);
      }

      const response = await fetch(`${API_BASE_URL}/organizations/public?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        organizations: data.data || [],
        pagination: data.pagination || {},
        success: data.success
      };
    } catch (error) {
      console.error('Error fetching public organizations:', error);
      throw error;
    }
  }

  /**
   * Get a specific public organization by ID
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object|null>} Organization object or null
   */
  static async getPublicOrganizationById(organizationId) {
    try {
      const response = await fetch(`${API_BASE_URL}/organizations/public/${organizationId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.success ? data.data : null;
    } catch (error) {
      console.error('Error fetching public organization:', error);
      throw error;
    }
  }

  /**
   * Search organizations by name (client-side filtering)
   * @param {Array} organizations - Array of organizations to filter
   * @param {string} query - Search query
   * @returns {Array} Filtered organizations
   */
  static filterOrganizations(organizations, query) {
    if (!query || !organizations.length) {
      return organizations;
    }

    const searchTerm = query.toLowerCase().trim();
    return organizations.filter(org => {
      // Search in name
      if (org.name && org.name.toLowerCase().includes(searchTerm)) {
        return true;
      }

      // Search in email
      if (org.email && org.email.toLowerCase().includes(searchTerm)) {
        return true;
      }

      // Search in address (handle both string and object)
      const addressString = this.formatAddress(org.address);
      if (addressString && addressString.toLowerCase().includes(searchTerm)) {
        return true;
      }

      // Search in phone
      if (org.phoneNumber && org.phoneNumber.toLowerCase().includes(searchTerm)) {
        return true;
      }

      // Search in services
      if (org.publicServices && Array.isArray(org.publicServices)) {
        const servicesMatch = org.publicServices.some(service => 
          service && service.toLowerCase().includes(searchTerm)
        );
        if (servicesMatch) {
          return true;
        }
      }

      return false;
    });
  }

  /**
   * Format organization data for display
   * @param {Object} organization - Raw organization data
   * @returns {Object} Formatted organization data
   */
  static formatOrganization(organization) {
    return {
      ...organization,
      displayName: organization.name || 'Nombre no disponible',
      displayAddress: this.formatAddress(organization.address),
      displayPhone: organization.phoneNumber || 'Teléfono no disponible',
      displayEmail: organization.email || 'Email no disponible',
      displayServices: organization.publicServices || [],
      displaySocialMediaLinks: this.formatSocialMediaLinks(organization.socialMediaLinks),
      createdDate: organization.createdAt ? new Date(organization.createdAt).toLocaleDateString('es-ES') : 'Fecha no disponible'
    };
  }

  /**
   * Format address object or string into a display string
   * @param {Object|string} address - Address data
   * @returns {string} Formatted address string
   */
  static formatAddress(address) {
    if (!address) {
      return 'Dirección no disponible';
    }

    // If address is already a string, return it
    if (typeof address === 'string') {
      return address;
    }

    // If address is an object, format it
    if (typeof address === 'object') {
      const parts = [];
      
      if (address.street) parts.push(address.street);
      if (address.city) parts.push(address.city);
      if (address.state) parts.push(address.state);
      if (address.zipCode) parts.push(address.zipCode);
      if (address.country) parts.push(address.country);
      
      return parts.length > 0 ? parts.join(', ') : 'Dirección no disponible';
    }

    return 'Dirección no disponible';
  }

  /**
   * Get a Google Maps search query from address
   * @param {Object|string} address - Address data
   * @returns {string} Address string suitable for Google Maps
   */
  static getAddressForMaps(address) {
    if (!address) return '';
    
    if (typeof address === 'string') {
      return address;
    }

    if (typeof address === 'object') {
      const parts = [];
      
      if (address.street) parts.push(address.street);
      if (address.city) parts.push(address.city);
      if (address.state) parts.push(address.state);
      if (address.zipCode) parts.push(address.zipCode);
      if (address.country) parts.push(address.country);
      
      return parts.join(', ');
    }

    return '';
  }

  /**
   * Format social media links for display
   * @param {Array} socialMediaLinks - Array of social media link objects
   * @returns {Array} Formatted social media links
   */
  static formatSocialMediaLinks(socialMediaLinks) {
    if (!socialMediaLinks || !Array.isArray(socialMediaLinks)) {
      return [];
    }

    return socialMediaLinks.map(link => ({
      ...link,
      displayName: this.getSocialMediaDisplayName(link.platform),
      iconClass: this.getSocialMediaIconClass(link.platform)
    }));
  }

  /**
   * Get display name for social media platform
   * @param {string} platform - Platform identifier
   * @returns {string} Display name
   */
  static getSocialMediaDisplayName(platform) {
    const platformNames = {
      website: 'Sitio Web',
      facebook: 'Facebook',
      instagram: 'Instagram',
      twitter: 'Twitter',
      linkedin: 'LinkedIn',
      youtube: 'YouTube',
      tiktok: 'TikTok'
    };
    
    return platformNames[platform] || platform.charAt(0).toUpperCase() + platform.slice(1);
  }

  /**
   * Get Font Awesome icon class for social media platform
   * @param {string} platform - Platform identifier
   * @returns {string} Icon class
   */
  static getSocialMediaIconClass(platform) {
    const iconClasses = {
      website: 'fas fa-globe',
      facebook: 'fab fa-facebook',
      instagram: 'fab fa-instagram',
      twitter: 'fab fa-twitter',
      linkedin: 'fab fa-linkedin',
      youtube: 'fab fa-youtube',
      tiktok: 'fab fa-tiktok'
    };
    
    return iconClasses[platform] || 'fas fa-link';
  }
}

export default PublicOrganizationService;